import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GoogleMapSection from "../components/GoogleMap";
import DateTimePicker from "../components/DateTimePicker";
import { LuCalendarDays, LuMail, LuAlarmClock, LuTruck, LuMinus, LuPlus } from "react-icons/lu";
import { TbKayak } from "react-icons/tb";
import { MdOutlineBookmarkAdd } from "react-icons/md";
import { RiMapPin2Line } from "react-icons/ri";
import { FaChevronLeft, FaChevronRight, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProfileModal from "../components/profile/ProfileModal";
import "../styles/Info.scss";
import { useLanguage } from "../context/LanguageContext";
import { getLocations } from "../api/locations";
import { getEvents } from "../api/events";
import { getPublicContentSettings } from "../api/publicSettings";
import { checkAvailability } from "../api/bookings";
import { toDateKey } from "../utils/dateUtils";
import { handleEventImageError, resolveEventImageUrl } from "../utils/eventImages";
import { buildLocationCoords, formatLocationAddress } from "../utils/locationUtils";
import { buildSelfAdjustingScheduleLabel } from "../utils/selfAdjustingScheduleLabel";
import { useNavigate } from "react-router-dom";

// Fix leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const INFO_HIGHLIGHTS = [
  {
    icon: LuAlarmClock,
    titleKey: "infoPage.highlights.0.title",
    titleFallback: "Flexible launch windows",
    descriptionKey: "infoPage.highlights.0.description",
    descriptionFallback: "Morning, afternoon and sunset paddles every day of the week.",
  },
  {
    icon: TbKayak,
    titleKey: "infoPage.highlights.1.title",
    titleFallback: "Boards & kayaks tuned",
    descriptionKey: "infoPage.highlights.1.description",
    descriptionFallback: "Freshly rinsed gear, glow-night kits, and optional extras to level up.",
  },
  {
    icon: LuTruck,
    titleKey: "infoPage.highlights.2.title",
    titleFallback: "Delivery on request",
    descriptionKey: "infoPage.highlights.2.description",
    descriptionFallback:
      "Delivery to your address can be arranged by email or WhatsApp for EUR 5, subject to agreement.",
  },
];

// Legacy translated event seed data kept temporarily while events move to admin-managed records.
// eslint-disable-next-line no-unused-vars
const EVENT_CONFIG = [
  {
    id: "sunset-paddle",
    image: "/assets/pos_main.jpg",
    titleKey: "infoPage.events.items.sunset.title",
    titleFallback: "Sunset Paddle Social",
    descriptionKey: "infoPage.events.items.sunset.description",
    descriptionFallback:
      "A relaxed golden-hour group paddle with easy pace, photo stops, and calm-water routing.",
    detailKey: "infoPage.events.items.sunset.detail",
    detailFallback:
      "Best for small groups, first-time paddlers, and anyone who wants a low-pressure evening on the water.",
    locationKey: "infoPage.events.items.sunset.location",
    locationFallback: "Danube Riverside",
    durationKey: "infoPage.events.items.sunset.duration",
    durationFallback: "2 hours",
    capacityKey: "infoPage.events.items.sunset.capacity",
    capacityFallback: "Up to 10 guests",
    priceKey: "infoPage.events.items.sunset.price",
    priceFallback: "From EUR 24",
    startDaysFromNow: 5,
    startTime: "18:30",
    endTime: "20:30",
    included: [
      {
        key: "infoPage.events.items.sunset.included.0",
        fallback: "SUP or kayak setup, paddle, life vest, and dry bag.",
      },
      {
        key: "infoPage.events.items.sunset.included.1",
        fallback: "Short safety briefing and route recommendation.",
      },
      {
        key: "infoPage.events.items.sunset.included.2",
        fallback: "Photo break at the calmest point of the route.",
      },
    ],
  },
  {
    id: "family-morning",
    image: "/assets/kayak_big.png",
    titleKey: "infoPage.events.items.family.title",
    titleFallback: "Family Morning Paddle",
    descriptionKey: "infoPage.events.items.family.description",
    descriptionFallback:
      "A quieter morning slot for families and mixed-experience groups who want clear timing and stable gear.",
    detailKey: "infoPage.events.items.family.detail",
    detailFallback:
      "We keep the route simple, fit vests before launch, and help families pick the right board or kayak.",
    locationKey: "infoPage.events.items.family.location",
    locationFallback: "Lake Vienna",
    durationKey: "infoPage.events.items.family.duration",
    durationFallback: "90 minutes",
    capacityKey: "infoPage.events.items.family.capacity",
    capacityFallback: "Up to 8 guests",
    priceKey: "infoPage.events.items.family.price",
    priceFallback: "From EUR 18",
    startDaysFromNow: 9,
    startTime: "09:30",
    endTime: "11:00",
    included: [
      {
        key: "infoPage.events.items.family.included.0",
        fallback: "Stable boards or kayaks selected for your group.",
      },
      {
        key: "infoPage.events.items.family.included.1",
        fallback: "Kid-sized life vests where needed.",
      },
      {
        key: "infoPage.events.items.family.included.2",
        fallback: "Simple pickup and return flow at the launch point.",
      },
    ],
  },
  {
    id: "glow-night",
    image: "/assets/glow_sup.png",
    titleKey: "infoPage.events.items.glow.title",
    titleFallback: "Glow Night Session",
    descriptionKey: "infoPage.events.items.glow.description",
    descriptionFallback:
      "A limited evening session with glow gear, darker water, and a more atmospheric group route.",
    detailKey: "infoPage.events.items.glow.detail",
    detailFallback:
      "Designed for confident swimmers who want a memorable night paddle with a small supervised crew.",
    locationKey: "infoPage.events.items.glow.location",
    locationFallback: "Danube Riverside",
    durationKey: "infoPage.events.items.glow.duration",
    durationFallback: "2 hours",
    capacityKey: "infoPage.events.items.glow.capacity",
    capacityFallback: "Up to 6 guests",
    priceKey: "infoPage.events.items.glow.price",
    priceFallback: "From EUR 34",
    startDaysFromNow: 14,
    startTime: "20:30",
    endTime: "22:30",
    included: [
      {
        key: "infoPage.events.items.glow.included.0",
        fallback: "LED SUP setup, paddle, vest, and dry bag.",
      },
      {
        key: "infoPage.events.items.glow.included.1",
        fallback: "Night-specific safety briefing before launch.",
      },
      {
        key: "infoPage.events.items.glow.included.2",
        fallback: "Small group size for a calmer route.",
      },
    ],
  },
  {
    id: "coffee-cruise",
    image: "/assets/sup1.png",
    titleKey: "infoPage.events.items.coffee.title",
    titleFallback: "Coffee Cruise",
    descriptionKey: "infoPage.events.items.coffee.description",
    descriptionFallback:
      "An easy late-morning paddle with a calm route and time to reset before the day gets busy.",
    detailKey: "infoPage.events.items.coffee.detail",
    detailFallback:
      "A low-intensity session for guests who want clear instruction, relaxed timing, and a short route.",
    locationKey: "infoPage.events.items.coffee.location",
    locationFallback: "Lake Vienna",
    durationKey: "infoPage.events.items.coffee.duration",
    durationFallback: "75 minutes",
    capacityKey: "infoPage.events.items.coffee.capacity",
    capacityFallback: "Up to 7 guests",
    priceKey: "infoPage.events.items.coffee.price",
    priceFallback: "From EUR 16",
    startDaysFromNow: 17,
    startTime: "10:30",
    endTime: "11:45",
    included: [
      {
        key: "infoPage.events.items.coffee.included.0",
        fallback: "Stable SUP setup with paddle, vest, and dry bag.",
      },
      {
        key: "infoPage.events.items.coffee.included.1",
        fallback: "Beginner-friendly pace and route briefing.",
      },
      {
        key: "infoPage.events.items.coffee.included.2",
        fallback: "Optional cafe stop recommendation after return.",
      },
    ],
  },
  {
    id: "team-paddle",
    image: "/assets/sup2.png",
    titleKey: "infoPage.events.items.team.title",
    titleFallback: "Team Paddle Hour",
    descriptionKey: "infoPage.events.items.team.description",
    descriptionFallback:
      "A compact team format for colleagues or friends who want shared gear and simple coordination.",
    detailKey: "infoPage.events.items.team.detail",
    detailFallback:
      "Built for teams that want a social activity without a complicated route, deposit, or long briefing.",
    locationKey: "infoPage.events.items.team.location",
    locationFallback: "Danube Riverside",
    durationKey: "infoPage.events.items.team.duration",
    durationFallback: "90 minutes",
    capacityKey: "infoPage.events.items.team.capacity",
    capacityFallback: "Up to 12 guests",
    priceKey: "infoPage.events.items.team.price",
    priceFallback: "From EUR 22",
    startDaysFromNow: 20,
    startTime: "17:00",
    endTime: "18:30",
    included: [
      {
        key: "infoPage.events.items.team.included.0",
        fallback: "Mixed SUP and kayak setup based on group size.",
      },
      {
        key: "infoPage.events.items.team.included.1",
        fallback: "Shared route plan and return timing.",
      },
      {
        key: "infoPage.events.items.team.included.2",
        fallback: "Group check-in support at the launch point.",
      },
    ],
  },
  {
    id: "skills-clinic",
    image: "/assets/sup_utils.png",
    titleKey: "infoPage.events.items.skills.title",
    titleFallback: "Balance Skills Clinic",
    descriptionKey: "infoPage.events.items.skills.description",
    descriptionFallback:
      "A focused SUP session for improving stance, turns, remounts, and confidence close to shore.",
    detailKey: "infoPage.events.items.skills.detail",
    detailFallback:
      "Good for returning paddlers who want better control before booking longer self-guided routes.",
    locationKey: "infoPage.events.items.skills.location",
    locationFallback: "Lake Vienna",
    durationKey: "infoPage.events.items.skills.duration",
    durationFallback: "90 minutes",
    capacityKey: "infoPage.events.items.skills.capacity",
    capacityFallback: "Up to 6 guests",
    priceKey: "infoPage.events.items.skills.price",
    priceFallback: "From EUR 28",
    startDaysFromNow: 23,
    startTime: "15:00",
    endTime: "16:30",
    included: [
      {
        key: "infoPage.events.items.skills.included.0",
        fallback: "SUP board, paddle, vest, and dry bag.",
      },
      {
        key: "infoPage.events.items.skills.included.1",
        fallback: "Technique-focused briefing before launch.",
      },
      {
        key: "infoPage.events.items.skills.included.2",
        fallback: "Small group size for more individual feedback.",
      },
    ],
  },
  {
    id: "kayak-discovery",
    image: "/assets/kayak.png",
    titleKey: "infoPage.events.items.discovery.title",
    titleFallback: "Kayak Discovery",
    descriptionKey: "infoPage.events.items.discovery.description",
    descriptionFallback:
      "A short guided kayak route for guests who want stable seating and simple paddling technique.",
    detailKey: "infoPage.events.items.discovery.detail",
    detailFallback:
      "Ideal for guests who prefer kayaking over standing on a SUP, with a route kept close to shore.",
    locationKey: "infoPage.events.items.discovery.location",
    locationFallback: "Lake Vienna",
    durationKey: "infoPage.events.items.discovery.duration",
    durationFallback: "2 hours",
    capacityKey: "infoPage.events.items.discovery.capacity",
    capacityFallback: "Up to 9 guests",
    priceKey: "infoPage.events.items.discovery.price",
    priceFallback: "From EUR 25",
    startDaysFromNow: 27,
    startTime: "13:00",
    endTime: "15:00",
    included: [
      {
        key: "infoPage.events.items.discovery.included.0",
        fallback: "Single or double kayak, paddle, vest, and dry bag.",
      },
      {
        key: "infoPage.events.items.discovery.included.1",
        fallback: "Basic steering and route briefing.",
      },
      {
        key: "infoPage.events.items.discovery.included.2",
        fallback: "Calm-water route selected for the day.",
      },
    ],
  },
  {
    id: "after-work",
    image: "/assets/day.png",
    titleKey: "infoPage.events.items.afterWork.title",
    titleFallback: "After Work Reset",
    descriptionKey: "infoPage.events.items.afterWork.description",
    descriptionFallback:
      "A clean one-hour reset after work with fast check-in, ready gear, and an easy return window.",
    detailKey: "infoPage.events.items.afterWork.detail",
    detailFallback:
      "Designed for people who want a short water break without committing to a full evening session.",
    locationKey: "infoPage.events.items.afterWork.location",
    locationFallback: "Danube Riverside",
    durationKey: "infoPage.events.items.afterWork.duration",
    durationFallback: "60 minutes",
    capacityKey: "infoPage.events.items.afterWork.capacity",
    capacityFallback: "Up to 10 guests",
    priceKey: "infoPage.events.items.afterWork.price",
    priceFallback: "From EUR 14",
    startDaysFromNow: 31,
    startTime: "18:00",
    endTime: "19:00",
    included: [
      {
        key: "infoPage.events.items.afterWork.included.0",
        fallback: "Fast pickup with SUP or kayak setup.",
      },
      {
        key: "infoPage.events.items.afterWork.included.1",
        fallback: "Compact route recommendation.",
      },
      {
        key: "infoPage.events.items.afterWork.included.2",
        fallback: "Return support before closing time.",
      },
    ],
  },
];

const LOCATION_CONFIG = [
  {
    id: "lake_vienna",
    coords: [48.2082, 16.3738],
    nameKey: "infoPage.locations.lakeVienna.name",
    nameFallback: "Lake Vienna",
    addressKey: "infoPage.locations.lakeVienna.address",
    addressFallback: "Near City Beach, Vienna",
    accent: "reef",
    highlightKey: "infoPage.locations.hours.lakeVienna.highlight",
    highlightFallback: "Sunrise city glides",
    noteKey: "infoPage.locations.hours.lakeVienna.note",
    noteFallback: "Early-bird coffee launches at 07:00 — call us to unlock dawn slots.",
    hours: [
      {
        labelKey: "infoPage.locations.hours.lakeVienna.weekdays.label",
        labelFallback: "Weekdays",
        valueKey: "infoPage.locations.hours.lakeVienna.weekdays.value",
        valueFallback: "07:30 – 20:30",
      },
      {
        labelKey: "infoPage.locations.hours.lakeVienna.saturday.label",
        labelFallback: "Saturday",
        valueKey: "infoPage.locations.hours.lakeVienna.saturday.value",
        valueFallback: "08:00 – 19:00",
      },
      {
        labelKey: "infoPage.locations.hours.lakeVienna.sunday.label",
        labelFallback: "Sunday & holidays",
        valueKey: "infoPage.locations.hours.lakeVienna.sunday.value",
        valueFallback: "09:00 – 18:00",
      },
    ],
  },
  {
    id: "danube_riverside",
    coords: [48.228, 16.41],
    nameKey: "infoPage.locations.danubeRiverside.name",
    nameFallback: "Danube Riverside",
    addressKey: "infoPage.locations.danubeRiverside.address",
    addressFallback: "Riverside Spot, Vienna",
    accent: "dawn",
    highlightKey: "infoPage.locations.hours.danube.highlight",
    highlightFallback: "Sunset party base",
    noteKey: "infoPage.locations.hours.danube.note",
    noteFallback: "Glow-night flotillas push off Fri & Sat at 20:30 — reserve ahead.",
    hours: [
      {
        labelKey: "infoPage.locations.hours.danube.weekdays.label",
        labelFallback: "Weekdays",
        valueKey: "infoPage.locations.hours.danube.weekdays.value",
        valueFallback: "08:00 – 21:00",
      },
      {
        labelKey: "infoPage.locations.hours.danube.saturday.label",
        labelFallback: "Saturday",
        valueKey: "infoPage.locations.hours.danube.saturday.value",
        valueFallback: "08:30 – 20:00",
      },
      {
        labelKey: "infoPage.locations.hours.danube.sunday.label",
        labelFallback: "Sunday & holidays",
        valueKey: "infoPage.locations.hours.danube.sunday.value",
        valueFallback: "08:30 – 18:30",
      },
    ],
  },
  {
    id: "mountain_lake",
    coords: [47.5, 13.4],
    nameKey: "infoPage.locations.mountainLake.name",
    nameFallback: "Mountain Lake",
    addressKey: "infoPage.locations.mountainLake.address",
    addressFallback: "Alpine Lake Resort",
    accent: "lunar",
    highlightKey: "infoPage.locations.hours.mountain.highlight",
    highlightFallback: "Alpine calm sessions",
    noteKey: "infoPage.locations.hours.mountain.note",
    noteFallback: "Closed Mon–Tue; private wellness retreats available on request.",
    hours: [
      {
        labelKey: "infoPage.locations.hours.mountain.midweek.label",
        labelFallback: "Wed – Fri",
        valueKey: "infoPage.locations.hours.mountain.midweek.value",
        valueFallback: "09:00 – 17:00",
      },
      {
        labelKey: "infoPage.locations.hours.mountain.saturday.label",
        labelFallback: "Saturday",
        valueKey: "infoPage.locations.hours.mountain.saturday.value",
        valueFallback: "08:30 – 18:00",
      },
      {
        labelKey: "infoPage.locations.hours.mountain.sunday.label",
        labelFallback: "Sunday",
        valueKey: "infoPage.locations.hours.mountain.sunday.value",
        valueFallback: "08:30 – 16:30",
      },
    ],
  },
];

const CONTACT_METHODS = [
  {
    labelKey: "infoPage.contact.items.email.label",
    labelFallback: "Drop a message",
    valueKey: "infoPage.contact.items.email.value",
    valueFallback: "wayli@wayli.eu",
    href: "mailto:wayli@wayli.eu",
    icon: LuMail,
  },
  {
    labelKey: "infoPage.contact.items.whatsapp.label",
    labelFallback: "Chat on WhatsApp",
    valueKey: "infoPage.contact.items.whatsapp.value",
    valueFallback: "wayli whatsapp",
    href: "https://wa.me/4368181542983",
    icon: FaWhatsapp,
  },
  {
    labelKey: "infoPage.contact.items.instagram.label",
    labelFallback: "Follow the vibes",
    valueKey: "infoPage.contact.items.instagram.value",
    valueFallback: "@wayli.eu",
    href: "https://www.instagram.com/wayli.eu",
    icon: FaInstagram,
  },
  {
    labelKey: "infoPage.contact.items.tiktok.label",
    labelFallback: "Join our community",
    valueKey: "infoPage.contact.items.tiktok.value",
    valueFallback: "tiktok.com/@wayli.eu",
    href: "https://www.tiktok.com/@wayli.eu?_r=1",
    icon: FaTiktok,
  },
];

const buildOperatingHoursDescription = (hours, t) => {
  if (!Array.isArray(hours) || hours.length === 0) return "";
  return hours
    .map((slot) => {
      const label = slot.labelKey
        ? t(slot.labelKey, slot.labelFallback)
        : slot.labelFallback;
      const value = slot.valueKey
        ? t(slot.valueKey, slot.valueFallback)
        : slot.valueFallback;
      return `${label} ${value}`;
    })
    .filter(Boolean)
    .join(", ");
};

const buildFallbackLocations = (t) =>
  LOCATION_CONFIG.map((item) => ({
    id: item.id,
    coords: item.coords,
    name: t(item.nameKey, item.nameFallback),
    address: t(item.addressKey, item.addressFallback),
    highlight: item.highlightKey
      ? t(item.highlightKey, item.highlightFallback)
      : item.highlightFallback,
    note: item.noteKey ? t(item.noteKey, item.noteFallback) : item.noteFallback,
    noteTone: item.noteTone ?? "soft",
    accent: item.accent,
    operatingHours: buildOperatingHoursDescription(item.hours, t),
  }));

const formatDurationLabel = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return "";
  const minutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${remainder}m`;
};

const formatEventPrice = (amount, currency = "EUR") =>
  `${currency} ${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;

const getMinutesOfDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? date.getHours() * 60 + date.getMinutes()
    : 0;

const buildMapsUrl = (location) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${location} Vienna`
  )}`;

const formatEventDate = (date, language) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(language === "de" ? "de-AT" : "en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatEventDateOnly = (date, language) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(language === "de" ? "de-AT" : "en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
};

const formatEventTimeRange = (startDate, endDate, language) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return "";
  const locale = language === "de" ? "de-AT" : "en-US";
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatter.format(startDate)}-${formatter.format(endDate)}`;
};

const buildDateWithMinutes = (date, minutes) => {
  const next = new Date(date);
  next.setHours(0, minutes, 0, 0);
  return next;
};

const DEFAULT_SELF_ADJUSTING_SCHEDULE = {
  weekdays: [1, 2, 3, 4, 5],
  pickupStartMinutes: 18 * 60,
  pickupEndMinutes: 20 * 60,
  returnStartMinutes: 18 * 60,
  returnEndMinutes: 22 * 60,
};

const normalizeSelfAdjustingSchedule = (schedule, startDate, endDate) => {
  const fallbackWeekdays = [];
  if (startDate instanceof Date && endDate instanceof Date) {
    const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const last = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    while (cursor <= last) {
      fallbackWeekdays.push(cursor.getDay());
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  const weekdays = Array.isArray(schedule?.weekdays)
    ? schedule.weekdays.map(Number).filter((day) => day >= 0 && day <= 6)
    : [];

  return {
    weekdays: [...new Set(weekdays.length ? weekdays : fallbackWeekdays.length ? fallbackWeekdays : DEFAULT_SELF_ADJUSTING_SCHEDULE.weekdays)],
    pickupStartMinutes: Number.isFinite(Number(schedule?.pickupStartMinutes))
      ? Math.max(0, Math.min(1439, Number(schedule.pickupStartMinutes)))
      : getMinutesOfDay(startDate),
    pickupEndMinutes: Number.isFinite(Number(schedule?.pickupEndMinutes))
      ? Math.max(0, Math.min(1439, Number(schedule.pickupEndMinutes)))
      : getMinutesOfDay(endDate),
    returnStartMinutes: Number.isFinite(Number(schedule?.returnStartMinutes))
      ? Math.max(0, Math.min(1439, Number(schedule.returnStartMinutes)))
      : getMinutesOfDay(startDate),
    returnEndMinutes: Number.isFinite(Number(schedule?.returnEndMinutes))
      ? Math.max(0, Math.min(1439, Number(schedule.returnEndMinutes)))
      : getMinutesOfDay(endDate),
  };
};

const getBoundedDayWindow = (occurrence, date, schedule) => {
  const dayStart = buildDateWithMinutes(date, 0);
  const dayEnd = buildDateWithMinutes(date, 1439);
  const occurrenceStart = occurrence.startDate > dayStart ? occurrence.startDate : dayStart;
  const occurrenceEnd = occurrence.endDate < dayEnd ? occurrence.endDate : dayEnd;
  const pickupStart = buildDateWithMinutes(date, schedule.pickupStartMinutes);
  const pickupEnd = buildDateWithMinutes(date, schedule.pickupEndMinutes);
  const returnStart = buildDateWithMinutes(date, schedule.returnStartMinutes);
  const returnEnd = buildDateWithMinutes(date, schedule.returnEndMinutes);

  return {
    pickupStartDate: occurrenceStart > pickupStart ? occurrenceStart : pickupStart,
    pickupEndDate: occurrenceEnd < pickupEnd ? occurrenceEnd : pickupEnd,
    returnStartDate: occurrenceStart > returnStart ? occurrenceStart : returnStart,
    returnEndDate: occurrenceEnd < returnEnd ? occurrenceEnd : returnEnd,
  };
};

const hasValidDayWindow = (occurrence, date, schedule) => {
  if (!schedule.weekdays.includes(date.getDay())) return false;
  const window = getBoundedDayWindow(occurrence, date, schedule);
  return (
    window.pickupEndDate >= window.pickupStartDate &&
    window.returnEndDate >= window.returnStartDate &&
    window.returnEndDate > window.pickupStartDate
  );
};

const listOccurrenceDateOptions = (occurrences, schedule = null) => {
  const unique = new Map();
  occurrences.forEach((occurrence) => {
    const cursor = new Date(
      occurrence.startDate.getFullYear(),
      occurrence.startDate.getMonth(),
      occurrence.startDate.getDate()
    );
    const last = new Date(
      occurrence.endDate.getFullYear(),
      occurrence.endDate.getMonth(),
      occurrence.endDate.getDate()
    );

    while (cursor <= last) {
      const date = new Date(cursor);
      const dateKey = toDateKey(date);
      if (!schedule || hasValidDayWindow(occurrence, date, schedule)) {
        unique.set(`${occurrence.id}:${dateKey}`, {
          value: `${occurrence.id}:${dateKey}`,
          occurrenceId: occurrence.id,
          date,
          dateKey,
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  });
  return [...unique.values()].sort((a, b) => a.date.getTime() - b.date.getTime());
};

const buildSelfAdjustingGroupKey = (event) =>
  [
    event.title,
    event.description,
    event.locationId,
    event.location,
    event.priceAmount,
    event.currency,
    event.capacity,
    event.equipmentTypeId || "",
    event.equipmentQuantityPerAttendee || 1,
    event.image,
    event.mapUrl,
    event.included.join("|"),
    JSON.stringify(event.selfAdjustingSchedule || {}),
    getMinutesOfDay(event.startDate),
    getMinutesOfDay(event.endDate),
  ].join("::");

const buildScheduleLabel = (event, language, t) => {
  const occurrences = event.occurrences?.length ? event.occurrences : [event];
  if (event.isSelfAdjusting) {
    const schedule = normalizeSelfAdjustingSchedule(
      event.selfAdjustingSchedule,
      event.startDate,
      event.endDate
    );
    return buildSelfAdjustingScheduleLabel(schedule, language);
  }
  if (occurrences.length <= 1) {
    return formatEventDate(event.startDate, language);
  }

  const first = occurrences[0];
  const allSameWindow = occurrences.every(
    (occurrence) =>
      getMinutesOfDay(occurrence.startDate) === getMinutesOfDay(first.startDate) &&
      getMinutesOfDay(occurrence.endDate) === getMinutesOfDay(first.endDate)
  );
  const allSameWeekday = occurrences.every(
    (occurrence) => occurrence.startDate.getDay() === first.startDate.getDay()
  );
  const countLabel = t("infoPage.events.scheduleCount", "{count} dates").replace(
    "{count}",
    String(occurrences.length)
  );
  const dateRange = `${formatEventDateOnly(first.startDate, language)}-${formatEventDateOnly(
    occurrences[occurrences.length - 1].startDate,
    language
  )}`;
  const timeRange = allSameWindow ? formatEventTimeRange(first.startDate, first.endDate, language) : "";

  if (allSameWeekday && timeRange) {
    const weekday = new Intl.DateTimeFormat(language === "de" ? "de-AT" : "en-US", {
      weekday: "long",
    }).format(first.startDate);
    return `${weekday} · ${countLabel} · ${timeRange}`;
  }

  return [dateRange, countLabel, timeRange].filter(Boolean).join(" · ");
};

const pickEventLocalizedText = (item, language) => {
  const primary = language === "de" ? item.descriptionDe : item.descriptionEn;
  const secondary = language === "de" ? item.descriptionEn : item.descriptionDe;
  return primary?.trim() || secondary?.trim() || item.description || "";
};

const pickEventLocalizedIncluded = (item, language) => {
  const primary = language === "de" ? item.includedDe : item.includedEn;
  const secondary = language === "de" ? item.includedEn : item.includedDe;
  if (Array.isArray(primary) && primary.length) return primary;
  if (Array.isArray(secondary) && secondary.length) return secondary;
  return Array.isArray(item.included) ? item.included : [];
};

const normalizeEventSummary = (item, t, language) => {
  const startDate = new Date(item.startUtc);
  const endDate = new Date(item.endUtc);
  const priceAmount = Number.parseFloat(item.priceAmount);
  const currency = item.currency || "EUR";
  const location = item.locationName || "";
  const description = pickEventLocalizedText(item, language);

  return {
    id: item.id,
    title: item.title || t("infoPage.events.fallbackTitle", "Event"),
    description,
    detail: description,
    location,
    duration: formatDurationLabel(startDate, endDate),
    capacity: `${item.capacity ?? 0} places`,
    availablePlaces: Number.parseInt(item.capacity, 10) || 0,
    bookedCount: Number.parseInt(item.bookedCount, 10) || 0,
    remainingPlaces: Number.parseInt(item.remainingPlaces, 10) || 0,
    price: formatEventPrice(priceAmount, currency),
    priceAmount: Number.isFinite(priceAmount) ? priceAmount : 0,
    currency,
    isSelfAdjusting: Boolean(item.isSelfAdjusting),
    mapUrl: item.mapUrl || buildMapsUrl(location),
    image: resolveEventImageUrl(item.imageUrl),
    startDate,
    endDate,
    locationId: item.locationId,
    equipmentTypeId: item.equipmentTypeId,
    equipmentTypeName: item.equipmentTypeName,
    equipmentOptions:
      Array.isArray(item.equipmentOptions) && item.equipmentOptions.length
        ? item.equipmentOptions
            .map((option) => ({
              equipmentTypeId: option.equipmentTypeId,
              equipmentTypeName: option.equipmentTypeName,
              availableQuantity: Number.isFinite(Number.parseInt(option.availableQuantity, 10))
                ? Math.max(0, Number.parseInt(option.availableQuantity, 10))
                : null,
            }))
            .filter((option) => option.equipmentTypeId)
        : item.equipmentTypeId
          ? [
              {
                equipmentTypeId: item.equipmentTypeId,
                equipmentTypeName:
                  item.equipmentTypeName || t("infoPage.events.modal.supBoards", "SUP boards"),
                availableQuantity: null,
              },
            ]
          : [],
    equipmentQuantityPerAttendee: item.equipmentQuantityPerAttendee,
    selfAdjustingSchedule: item.selfAdjustingSchedule || null,
    included: pickEventLocalizedIncluded(item, language),
  };
};

const withScheduleLabel = (event, language, t) => ({
  ...event,
  scheduleLabel: buildScheduleLabel(event, language, t),
});

const EventBookingModal = ({ event, language, onClose, onCheckout, t }) => {
  const eventOccurrences = useMemo(
    () =>
      (event.occurrences?.length ? event.occurrences : [event])
        .map((occurrence) => ({
          ...occurrence,
          startDate: new Date(occurrence.startDate),
          endDate: new Date(occurrence.endDate),
        }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime()),
    [event]
  );
  const selfAdjustingSchedule = useMemo(
    () =>
      normalizeSelfAdjustingSchedule(
        event.selfAdjustingSchedule,
        eventOccurrences[0]?.startDate || event.startDate,
        eventOccurrences[eventOccurrences.length - 1]?.endDate || event.endDate
      ),
    [event.endDate, event.selfAdjustingSchedule, event.startDate, eventOccurrences]
  );
  const dateOptions = useMemo(
    () =>
      listOccurrenceDateOptions(
        eventOccurrences,
        event.isSelfAdjusting ? selfAdjustingSchedule : null
      ),
    [event.isSelfAdjusting, eventOccurrences, selfAdjustingSchedule]
  );
  const [selectedDateOption, setSelectedDateOption] = useState(
    () => dateOptions[0]?.value || ""
  );
  const equipmentOptions = useMemo(
    () =>
      (event.equipmentOptions?.length
        ? event.equipmentOptions
        : event.equipmentTypeId
          ? [
              {
                equipmentTypeId: event.equipmentTypeId,
                equipmentTypeName:
                  event.equipmentTypeName ||
                  t("infoPage.events.modal.supBoards", "SUP boards"),
                availableQuantity: null,
              },
            ]
          : []
      ).filter((option) => option.equipmentTypeId),
    [event.equipmentOptions, event.equipmentTypeId, event.equipmentTypeName, t]
  );
  const equipmentOptionsKey = equipmentOptions
    .map((option) => option.equipmentTypeId)
    .join("|");
  const baselineAvailableByEquipment = useMemo(() => {
    const next = {};
    equipmentOptions.forEach((option) => {
      const available = Number.parseInt(option.availableQuantity, 10);
      if (Number.isFinite(available)) {
        next[option.equipmentTypeId] = Math.max(0, available);
      }
    });
    return next;
  }, [equipmentOptions]);
  const [selectedEquipmentQuantities, setSelectedEquipmentQuantities] = useState({});
  const [inventoryAvailableByEquipment, setInventoryAvailableByEquipment] = useState({});
  const selectedDateDetails =
    dateOptions.find((option) => option.value === selectedDateOption) || dateOptions[0];
  const selectedOccurrence =
    eventOccurrences.find((occurrence) => occurrence.id === selectedDateDetails?.occurrenceId) ||
    eventOccurrences[0] ||
    event;
  const activeStartDate = selectedOccurrence.startDate;
  const activeEndDate = selectedOccurrence.endDate;
  const selectedServiceDate = selectedDateDetails?.date || activeStartDate;
  const serviceDateKey = selectedDateDetails?.dateKey || toDateKey(activeStartDate);
  const { serviceStartDate, serviceEndDate } = useMemo(() => {
    const selectedDayStart = buildDateWithMinutes(selectedServiceDate, 0);
    const selectedDayEnd = buildDateWithMinutes(selectedServiceDate, 1439);
    return {
      serviceStartDate: activeStartDate > selectedDayStart ? activeStartDate : selectedDayStart,
      serviceEndDate: activeEndDate < selectedDayEnd ? activeEndDate : selectedDayEnd,
    };
  }, [activeEndDate, activeStartDate, selectedServiceDate]);
  const {
    pickupStartDate,
    pickupEndDate,
    returnStartDate,
    returnEndDate,
  } = useMemo(
    () =>
      event.isSelfAdjusting
        ? getBoundedDayWindow(selectedOccurrence, selectedServiceDate, selfAdjustingSchedule)
        : {
            pickupStartDate: serviceStartDate,
            pickupEndDate: serviceEndDate,
            returnStartDate: serviceStartDate,
            returnEndDate: serviceEndDate,
          },
    [
      event.isSelfAdjusting,
      selectedOccurrence,
      selectedServiceDate,
      selfAdjustingSchedule,
      serviceEndDate,
      serviceStartDate,
    ]
  );
  const [pickupDate, setPickupDate] = useState(() => serviceStartDate);
  const [returnDate, setReturnDate] = useState(() => serviceEndDate);
  const [showWindowError, setShowWindowError] = useState(false);
  const [draggingPicker, setDraggingPicker] = useState(null);
  const datePickerStartDate = dateOptions[0]?.date || eventOccurrences[0]?.startDate || activeStartDate;
  const datePickerEndDate =
    dateOptions[dateOptions.length - 1]?.date ||
    eventOccurrences[eventOccurrences.length - 1]?.endDate ||
    activeEndDate;
  const datePickerBlockedDates = useMemo(() => {
    const allowed = new Set(dateOptions.map((option) => option.dateKey));
    if (!datePickerStartDate || !datePickerEndDate || allowed.size === 0) return [];

    const blocked = [];
    const cursor = new Date(
      datePickerStartDate.getFullYear(),
      datePickerStartDate.getMonth(),
      datePickerStartDate.getDate()
    );
    const last = new Date(
      datePickerEndDate.getFullYear(),
      datePickerEndDate.getMonth(),
      datePickerEndDate.getDate()
    );

    while (cursor <= last) {
      const key = toDateKey(cursor);
      if (!allowed.has(key)) {
        blocked.push(key);
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return blocked;
  }, [dateOptions, datePickerEndDate, datePickerStartDate]);
  const displayStartDate = event.isSelfAdjusting ? pickupDate : activeStartDate;
  const displayEndDate = event.isSelfAdjusting ? returnDate : activeEndDate;
  const formattedStart = formatEventDate(displayStartDate, language);
  const formattedEnd = formatEventDate(displayEndDate, language);
  const selectedDuration = formatDurationLabel(pickupDate, returnDate);
  const selectedAvailablePlaces = selectedOccurrence.availablePlaces ?? event.availablePlaces;
  const selectedBookedCount = selectedOccurrence.bookedCount ?? event.bookedCount;
  const selectedRemainingPlaces = selectedOccurrence.remainingPlaces ?? event.remainingPlaces;
  const capacityRemainingQuantity = Math.max(0, selectedRemainingPlaces);
  const selectedEquipmentItems = equipmentOptions
    .map((option) => ({
      equipmentTypeId: option.equipmentTypeId,
      equipmentTypeName: option.equipmentTypeName,
      quantity: Number.parseInt(selectedEquipmentQuantities[option.equipmentTypeId], 10) || 0,
    }))
    .filter((item) => item.quantity > 0);
  const selectedBoardQuantity = event.isSelfAdjusting
    ? selectedEquipmentItems.reduce((sum, item) => sum + item.quantity, 0)
    : 1;
  const capacityPercent = selectedAvailablePlaces
    ? Math.min(100, Math.round((selectedBookedCount / selectedAvailablePlaces) * 100))
    : 0;
  const totalPrice = event.priceAmount * selectedBoardQuantity;
  const pickupStartMinutes = getMinutesOfDay(pickupStartDate);
  const pickupEndMinutes = getMinutesOfDay(pickupEndDate);
  const returnStartMinutes = getMinutesOfDay(returnStartDate);
  const returnEndMinutes = getMinutesOfDay(returnEndDate);
  const hasBookableDates = !event.isSelfAdjusting || dateOptions.length > 0;
  const hasValidCustomWindow =
    !event.isSelfAdjusting ||
    (pickupDate instanceof Date &&
      returnDate instanceof Date &&
      !Number.isNaN(pickupDate.getTime()) &&
      !Number.isNaN(returnDate.getTime()) &&
      returnDate > pickupDate &&
      toDateKey(pickupDate) === serviceDateKey &&
      toDateKey(returnDate) === serviceDateKey &&
      pickupDate >= pickupStartDate &&
      pickupDate <= pickupEndDate &&
      returnDate >= returnStartDate &&
      returnDate <= returnEndDate);

  useEffect(() => {
    if (hasValidCustomWindow) {
      setShowWindowError(false);
    }
  }, [hasValidCustomWindow]);

  const getEquipmentAvailableQuantity = useCallback(
    (equipmentTypeId) => {
      const value = inventoryAvailableByEquipment[equipmentTypeId];
      if (Number.isFinite(value)) return Math.max(0, value);
      const baseline = baselineAvailableByEquipment[equipmentTypeId];
      return Number.isFinite(baseline) ? Math.max(0, baseline) : 0;
    },
    [baselineAvailableByEquipment, inventoryAvailableByEquipment]
  );

  const getEquipmentMaxQuantity = useCallback(
    (equipmentTypeId) => {
      const selectedForOtherItems = selectedEquipmentItems
        .filter((item) => item.equipmentTypeId !== equipmentTypeId)
        .reduce((sum, item) => sum + item.quantity, 0);
      return Math.max(
        0,
        Math.min(
          getEquipmentAvailableQuantity(equipmentTypeId),
          capacityRemainingQuantity - selectedForOtherItems
        )
      );
    },
    [capacityRemainingQuantity, getEquipmentAvailableQuantity, selectedEquipmentItems]
  );

  const updateEquipmentQuantity = (equipmentTypeId, delta) => {
    setSelectedEquipmentQuantities((current) => {
      const currentQuantity = Number.parseInt(current[equipmentTypeId], 10) || 0;
      const maxQuantity = getEquipmentMaxQuantity(equipmentTypeId);
      return {
        ...current,
        [equipmentTypeId]: Math.max(0, Math.min(maxQuantity, currentQuantity + delta)),
      };
    });
  };

  const handlePickerDragStateChange = useCallback((pickerId, isDragging) => {
    setDraggingPicker((current) => {
      if (isDragging) return pickerId;
      return current === pickerId ? null : current;
    });
  }, []);

  useEffect(() => {
    setSelectedDateOption(dateOptions[0]?.value || "");
  }, [dateOptions, event.id]);

  useEffect(() => {
    setPickupDate((current) => {
      if (
        current instanceof Date &&
        !Number.isNaN(current.getTime()) &&
        toDateKey(current) === serviceDateKey
      ) {
        if (current < pickupStartDate) return pickupStartDate;
        if (current > pickupEndDate) return pickupEndDate;
        return current;
      }
      return pickupStartDate;
    });
    setReturnDate((current) => {
      if (
        current instanceof Date &&
        !Number.isNaN(current.getTime()) &&
        toDateKey(current) === serviceDateKey
      ) {
        if (current < returnStartDate) return returnStartDate;
        if (current > returnEndDate) return returnEndDate;
        return current;
      }
      return returnEndDate;
    });
  }, [
    pickupEndDate,
    pickupStartDate,
    returnEndDate,
    returnStartDate,
    selectedDateOption,
    serviceDateKey,
  ]);

  const handlePickupSelectionChange = useCallback(
    (nextDate) => {
      if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) return;
      const nextDateKey = toDateKey(nextDate);
      const matchingOption =
        dateOptions.find((option) => option.dateKey === nextDateKey) || dateOptions[0];
      if (matchingOption && matchingOption.value !== selectedDateOption) {
        setSelectedDateOption(matchingOption.value);
      }
      setPickupDate(nextDate);
    },
    [dateOptions, selectedDateOption]
  );

  const handleEventDateSelectionChange = useCallback(
    (nextDate) => {
      if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) return;
      const nextDateKey = toDateKey(nextDate);
      const matchingOption =
        dateOptions.find((option) => option.dateKey === nextDateKey) || dateOptions[0];
      if (matchingOption && matchingOption.value !== selectedDateOption) {
        setSelectedDateOption(matchingOption.value);
      }

      setPickupDate((current) => {
        const minutes = getMinutesOfDay(current instanceof Date ? current : pickupStartDate);
        return buildDateWithMinutes(nextDate, minutes);
      });
      setReturnDate((current) => {
        const minutes = getMinutesOfDay(current instanceof Date ? current : returnEndDate);
        return buildDateWithMinutes(nextDate, minutes);
      });
    },
    [dateOptions, pickupStartDate, returnEndDate, selectedDateOption]
  );

  useEffect(() => {
    if (!event.isSelfAdjusting) return;
    setSelectedEquipmentQuantities((current) => {
      const next = {};
      equipmentOptions.forEach((option, index) => {
        const currentQuantity = Number.parseInt(current[option.equipmentTypeId], 10) || 0;
        next[option.equipmentTypeId] = currentQuantity;
        if (index === 0 && !Object.values(current).some((value) => Number.parseInt(value, 10) > 0)) {
          next[option.equipmentTypeId] = 1;
        }
      });
      return next;
    });
  }, [equipmentOptions, equipmentOptionsKey, event.isSelfAdjusting]);

  useEffect(() => {
    if (!event.isSelfAdjusting || !event.locationId || equipmentOptions.length === 0) {
      setInventoryAvailableByEquipment({});
      return undefined;
    }

    if (draggingPicker) {
      return undefined;
    }

    if (!hasValidCustomWindow) {
      setInventoryAvailableByEquipment({});
      return undefined;
    }

    let cancelled = false;
    const availabilityTimer = window.setTimeout(() => {
      checkAvailability({
        locationId: event.locationId,
        startUtc: pickupDate.toISOString(),
        endUtc: returnDate.toISOString(),
        items: equipmentOptions.map((option) => ({
          equipmentTypeId: option.equipmentTypeId,
          quantity: 1,
        })),
      })
        .then((payload) => {
          if (cancelled) return;
          const nextAvailability = {};
          if (Array.isArray(payload?.items)) {
            payload.items.forEach((item) => {
              const available = Number.parseInt(item?.availableQuantity, 10);
              if (item?.equipmentTypeId && Number.isFinite(available)) {
                nextAvailability[item.equipmentTypeId] = Math.max(0, available);
              }
            });
          }
          setInventoryAvailableByEquipment(nextAvailability);
        })
        .catch(() => {
          if (!cancelled) {
            setInventoryAvailableByEquipment({});
          }
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(availabilityTimer);
    };
  }, [
    draggingPicker,
    equipmentOptions,
    equipmentOptionsKey,
    event.isSelfAdjusting,
    event.locationId,
    hasValidCustomWindow,
    pickupDate,
    returnDate,
  ]);

  useEffect(() => {
    if (!event.isSelfAdjusting) return;
    setSelectedEquipmentQuantities((current) => {
      let changed = false;
      let used = 0;
      const next = {};
      equipmentOptions.forEach((option) => {
        const available = getEquipmentAvailableQuantity(option.equipmentTypeId);
        const remainingForThis = Math.max(0, capacityRemainingQuantity - used);
        const maxForThis = Math.min(available, remainingForThis);
        const currentQuantity = Number.parseInt(current[option.equipmentTypeId], 10) || 0;
        const clamped = Math.max(0, Math.min(currentQuantity, maxForThis));
        next[option.equipmentTypeId] = clamped;
        used += clamped;
        if (clamped !== currentQuantity) changed = true;
      });
      return changed ? next : current;
    });
  }, [
    capacityRemainingQuantity,
    equipmentOptions,
    equipmentOptionsKey,
    event.isSelfAdjusting,
    getEquipmentAvailableQuantity,
    inventoryAvailableByEquipment,
  ]);

  return (
    <ProfileModal
      title={event.title}
      onClose={onClose}
      descriptionId="info-event-modal-description"
    >
      <div className="info-event-modal">
        <div className="info-event-modal__hero">
          <div className="info-event-modal__visual">
            <img src={event.image} alt={event.title} onError={handleEventImageError} />
          </div>
          <div className="info-event-modal__intro">
            <p className="info-event-modal__eyebrow">
              {t("infoPage.events.modal.eventDetails", "Event details")}
            </p>
            <p id="info-event-modal-description" className="info-event-modal__description">
              {event.detail}
            </p>
            <p className="info-event-modal__secondary">{event.description}</p>
          </div>
        </div>

        <div className="info-event-modal__facts">
          <div className="info-event-modal__fact">
            <span>{t("infoPage.events.modal.time", "Time")}</span>
            <strong>{formattedStart}</strong>
            <small>{formattedEnd}</small>
          </div>
          <a
            className="info-event-modal__fact info-event-modal__fact--link"
            href={event.mapUrl}
            target="_blank"
            rel="noreferrer"
          >
            <span>{t("infoPage.events.modal.location", "Location")}</span>
            <strong>{event.location}</strong>
            <small>{t("infoPage.events.modal.openMaps", "Open in Google Maps")}</small>
          </a>
          <div className="info-event-modal__fact">
            <span>{t("infoPage.events.modal.duration", "Duration")}</span>
            <strong>{event.isSelfAdjusting ? selectedDuration : event.duration}</strong>
            <small>{event.price}</small>
          </div>
        </div>

        {event.isSelfAdjusting && (
          <div className="info-event-modal__time-choice">
            <div>
              <h4>{t("infoPage.events.modal.pickupReturn", "Choose pickup and return")}</h4>
              <p>
                {t(
                  "infoPage.events.modal.pickupReturnHint",
                  "Pick any available time inside the event window."
                )}
              </p>
            </div>
            {!hasBookableDates && (
              <p className="contact-hint">
                {t(
                  "infoPage.events.modal.noBookableDates",
                  "No bookable dates are configured for this event."
                )}
              </p>
            )}
            <div className="info-event-modal__pickers info-event-modal__pickers--self-adjusting">
              <div className="info-event-modal__picker-panel info-event-modal__picker-panel--date">
                <p className="info-event-modal__picker-label">
                  {t("infoPage.events.modal.eventDate", "Event date")}
                </p>
                <DateTimePicker
                  initialDate={pickupDate}
                  onSelectionChange={handleEventDateSelectionChange}
                  showDurationControls={false}
                  showTimePicker={false}
                  syncInitialTime={false}
                  minMinutes={pickupStartMinutes}
                  maxMinutes={pickupEndMinutes}
                  selectableStartDate={datePickerStartDate}
                  selectableStartDateTime={pickupStartDate}
                  selectableEndDate={datePickerEndDate}
                    blockedDates={datePickerBlockedDates}
                    timelineStartMinutes={pickupStartMinutes}
                    timelineEndMinutes={pickupEndMinutes}
                    stepMinutes={10}
                  />
              </div>
              <div className="info-event-modal__time-stack">
                <div className="info-event-modal__picker-panel info-event-modal__picker-panel--pickup">
                  <p className="info-event-modal__picker-label">
                    {t("infoPage.events.modal.pickup", "Pickup")}
                  </p>
                  <DateTimePicker
                    initialDate={pickupDate}
                    onSelectionChange={handlePickupSelectionChange}
                    showDurationControls={false}
                    minMinutes={pickupStartMinutes}
                    maxMinutes={pickupEndMinutes}
                    selectableStartDate={datePickerStartDate}
                    selectableStartDateTime={pickupStartDate}
                    selectableEndDate={datePickerEndDate}
                    blockedDates={datePickerBlockedDates}
                    timelineStartMinutes={pickupStartMinutes}
                    timelineEndMinutes={pickupEndMinutes}
                    stepMinutes={10}
                    onDragStateChange={(isDragging) =>
                      handlePickerDragStateChange("pickup", isDragging)
                    }
                  />
                </div>
                <div className="info-event-modal__picker-panel info-event-modal__picker-panel--return">
                  <p className="info-event-modal__picker-label">
                    {t("infoPage.events.modal.return", "Return")}
                  </p>
                  <DateTimePicker
                    initialDate={returnDate}
                    onSelectionChange={setReturnDate}
                    showDurationControls={false}
                    minMinutes={returnStartMinutes}
                    maxMinutes={returnEndMinutes}
                    selectableStartDate={selectedServiceDate}
                    selectableStartDateTime={returnStartDate}
                    selectableEndDate={selectedServiceDate}
                    timelineStartMinutes={returnStartMinutes}
                    timelineEndMinutes={returnEndMinutes}
                    stepMinutes={10}
                    onDragStateChange={(isDragging) =>
                      handlePickerDragStateChange("return", isDragging)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {event.isSelfAdjusting ? (
          <div className="info-event-modal__equipment-choice">
            <div className="info-event-modal__equipment-heading">
              <span>{t("infoPage.events.modal.equipment", "Equipment")}</span>
              <strong>{t("infoPage.events.modal.chooseEquipment", "Choose what you need")}</strong>
              <small>
                {t("infoPage.events.modal.totalSelected", "{count} selected").replace(
                  "{count}",
                  String(selectedBoardQuantity)
                )}
              </small>
            </div>
            {equipmentOptions.map((option) => {
              const currentQuantity =
                Number.parseInt(selectedEquipmentQuantities[option.equipmentTypeId], 10) || 0;
              const maxQuantity = getEquipmentMaxQuantity(option.equipmentTypeId);
              return (
                <div className="info-event-modal__board-choice" key={option.equipmentTypeId}>
                  <div>
                    <span>{t("infoPage.events.modal.boards", "Boards")}</span>
                    <strong>{option.equipmentTypeName}</strong>
                    <small>
                      {t("infoPage.events.modal.boardsLeft", "{count} available").replace(
                        "{count}",
                        String(maxQuantity)
                      )}
                    </small>
                  </div>
                  <div
                    className="info-event-modal__quantity"
                    aria-label={t("infoPage.events.modal.boardQuantity", "Board quantity")}
                  >
                    <button
                      type="button"
                      onClick={() => updateEquipmentQuantity(option.equipmentTypeId, -1)}
                      disabled={currentQuantity <= 0}
                    >
                      <LuMinus aria-hidden="true" />
                    </button>
                    <strong>{currentQuantity}</strong>
                    <button
                      type="button"
                      onClick={() => updateEquipmentQuantity(option.equipmentTypeId, 1)}
                      disabled={currentQuantity >= maxQuantity || maxQuantity <= 0}
                    >
                      <LuPlus aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="info-event-modal__capacity">
            <div>
              <span>{t("infoPage.events.modal.availability", "Availability")}</span>
              <strong>
                {t(
                  "infoPage.events.modal.bookedCount",
                  "{booked} booked of {total} places"
                )
                  .replace("{booked}", String(selectedBookedCount))
                  .replace("{total}", String(selectedAvailablePlaces))}
              </strong>
            </div>
            <span>{selectedRemainingPlaces} {t("infoPage.events.modal.left", "left")}</span>
            <div className="info-event-modal__capacity-track" aria-hidden="true">
              <span style={{ width: `${capacityPercent}%` }} />
            </div>
          </div>
        )}

        <div className="info-event-modal__included">
          <h4>{t("infoPage.events.modal.included", "Included")}</h4>
          <ul>
            {event.included.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="info-event-modal__actions">
          <div className="info-event-modal__total">
            <span>{t("infoPage.events.modal.total", "Total")}</span>
            <strong>EUR {totalPrice.toFixed(2)}</strong>
          </div>
          <p
            className={`info-event-modal__validation ${
              showWindowError ? "is-visible" : ""
            }`}
          >
            {t(
              "infoPage.events.modal.invalidPickupReturn",
              "Return time must be after pickup time, and both times must be inside the event window."
            )}
          </p>
          <button
            type="button"
            className="info-event-btn info-event-btn--primary"
            onClick={() => {
              if (!hasValidCustomWindow) {
                setShowWindowError(true);
                return;
              }

              onCheckout({
                event: {
                  ...event,
                  id: selectedOccurrence.id,
                  startDate: activeStartDate,
                  endDate: activeEndDate,
                  duration: formatDurationLabel(activeStartDate, activeEndDate),
                  availablePlaces: selectedAvailablePlaces,
                  bookedCount: selectedBookedCount,
                  remainingPlaces: selectedRemainingPlaces,
                  quantity: event.isSelfAdjusting ? selectedBoardQuantity : 1,
                  equipmentItems: event.isSelfAdjusting
                    ? selectedEquipmentItems.map((item) => ({
                        equipmentTypeId: item.equipmentTypeId,
                        quantity: item.quantity,
                      }))
                    : [],
                  equipmentSummary: event.isSelfAdjusting
                    ? selectedEquipmentItems.map((item) => ({
                        name: item.equipmentTypeName,
                        quantity: item.quantity,
                      }))
                    : [],
                },
                selectedStartDate: event.isSelfAdjusting ? pickupDate : event.startDate,
                selectedEndDate: event.isSelfAdjusting ? returnDate : event.endDate,
                totalPrice,
              });
            }}
            disabled={
              !selectedRemainingPlaces ||
              !hasBookableDates ||
              (event.isSelfAdjusting &&
                (equipmentOptions.length === 0 || selectedBoardQuantity <= 0))
            }
          >
            <MdOutlineBookmarkAdd aria-hidden="true" />
            {t("infoPage.events.modal.request", "Request booking")}
          </button>
        </div>
      </div>
    </ProfileModal>
  );
};

const InfoPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const eventsRailRef = useRef(null);
  const eventsDragRef = useRef({
    isDragging: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
    resetTimer: null,
  });

  const fallbackLocations = useMemo(() => buildFallbackLocations(t), [t]);
  const [liveLocations, setLiveLocations] = useState(null);
  const [useFallback, setUseFallback] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState("");
  const [liveEvents, setLiveEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [, setEventsError] = useState("");
  const [publicContent, setPublicContent] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const hasLoadedLiveRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadPublicContent = async () => {
      try {
        const payload = await getPublicContentSettings();
        if (!cancelled) {
          setPublicContent(payload && typeof payload === "object" ? payload : {});
        }
      } catch {
        if (!cancelled) {
          setPublicContent({});
        }
      }
    };

    loadPublicContent();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      setLocationsLoading(true);
      setLocationsError("");
      try {
        const payload = await getLocations();
        if (cancelled) return;
        const items = Array.isArray(payload) ? payload : [];
        const normalized = items.map((location) => ({
          id: location.id,
          name: location.name,
          address: formatLocationAddress(location),
          coords: buildLocationCoords(location.latitude, location.longitude),
          operatingHours: location.operatingHours?.trim() ?? "",
          highlight: null,
          note: null,
          noteTone: "soft",
          accent: "base",
        }));
        if (cancelled) return;
        setLiveLocations(normalized);
        setUseFallback(false);
        hasLoadedLiveRef.current = true;
      } catch (error) {
        if (cancelled) return;
        if (!hasLoadedLiveRef.current) {
          setUseFallback(true);
        }
        setLocationsError(
          error?.message ??
            t("infoPage.locations.loadError", "Unable to load locations right now.")
        );
      } finally {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      setEventsLoading(true);
      setEventsError("");
      try {
        const payload = await getEvents();
        if (cancelled) return;
        setLiveEvents(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (cancelled) return;
        setLiveEvents([]);
        setEventsError(
          error?.message ??
            t("infoPage.events.loadError", "Unable to load events right now.")
        );
      } finally {
        if (!cancelled) {
          setEventsLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const locations = useMemo(
    () => (useFallback ? fallbackLocations : liveLocations ?? []),
    [fallbackLocations, liveLocations, useFallback]
  );

  useEffect(() => {
    if (!locations.length) {
      setSelectedLocationId(null);
      return;
    }
    if (selectedLocationId && locations.some((loc) => loc.id === selectedLocationId)) {
      return;
    }
    setSelectedLocationId(locations[0].id);
  }, [locations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    if (!locations.length) return null;
    return (
      locations.find((location) => location.id === selectedLocationId) ?? locations[0]
    );
  }, [locations, selectedLocationId]);

  const selectedLocationMapUrl =
    selectedLocation?.coords?.length === 2
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          `${selectedLocation.coords[0]},${selectedLocation.coords[1]}`
        )}`
      : null;
  const operatingHoursLines = useMemo(() => {
    if (!selectedLocation?.operatingHours) return [];
    return selectedLocation.operatingHours
      .split(";")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }, [selectedLocation?.operatingHours]);

  const splitOperatingHoursLine = (line) => {
    const match = line.match(/(\d{1,2}:\d{2}.*)$/);
    if (!match) {
      return { day: line, time: "" };
    }
    const time = match[1].trim();
    const day = line.slice(0, match.index).replace(/[,:-–—]\s*$/, "").trim();
    return { day, time };
  };

  const contactDetails = useMemo(
    () =>
      CONTACT_METHODS.map((method) => ({
        label: t(method.labelKey, method.labelFallback),
        value: t(method.valueKey, method.valueFallback),
        href: method.href,
        icon: method.icon,
      })),
    [t]
  );

  const infoHeroDescription = useMemo(() => {
    const localizedText = publicContent?.infoPageText || {};
    const currentText =
      typeof localizedText[language] === "string" ? localizedText[language].trim() : "";
    const fallbackLanguage = language === "de" ? "en" : "de";
    const fallbackText =
      typeof localizedText[fallbackLanguage] === "string"
        ? localizedText[fallbackLanguage].trim()
        : "";

    return (
      currentText ||
      fallbackText ||
      t(
        "infoPage.hero.description",
        "Opening hours, launch spots and how to reach us. Settle all the details, then roll up ready to float."
      )
    );
  }, [language, publicContent?.infoPageText, t]);

  const events = useMemo(() => {
    const normalized = liveEvents
      .map((item) => normalizeEventSummary(item, t, language))
      .filter(
        (item) =>
          item.startDate instanceof Date &&
          !Number.isNaN(item.startDate.getTime()) &&
          item.endDate instanceof Date &&
          !Number.isNaN(item.endDate.getTime())
      )
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    const grouped = [];
    const selfAdjustingGroups = new Map();

    normalized.forEach((event) => {
      if (!event.isSelfAdjusting) {
        grouped.push(withScheduleLabel({ ...event, occurrences: [event] }, language, t));
        return;
      }

      const groupKey = buildSelfAdjustingGroupKey(event);
      const existing = selfAdjustingGroups.get(groupKey);
      if (existing) {
        existing.occurrences.push(event);
        existing.startDate =
          event.startDate < existing.startDate ? event.startDate : existing.startDate;
        existing.endDate = event.endDate > existing.endDate ? event.endDate : existing.endDate;
        existing.availablePlaces += event.availablePlaces;
        existing.bookedCount += event.bookedCount;
        existing.remainingPlaces += event.remainingPlaces;
        return;
      }

      const group = {
        ...event,
        id: `self-adjusting-${groupKey}`,
        occurrences: [event],
      };
      selfAdjustingGroups.set(groupKey, group);
      grouped.push(group);
    });

    return grouped
      .map((event) =>
        withScheduleLabel(
          {
            ...event,
            occurrences: [...event.occurrences].sort(
              (a, b) => a.startDate.getTime() - b.startDate.getTime()
            ),
          },
          language,
          t
        )
      )
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [language, liveEvents, t]);

  const handleEventCheckout = ({ event, selectedStartDate, selectedEndDate, totalPrice }) => {
    const startDate = selectedStartDate || event.startDate;
    const endDate = selectedEndDate || event.endDate;
    navigate("/booking-day", {
      state: {
        eventCheckout: {
          id: event.id,
          title: event.title,
          description: event.detail,
          location: event.location,
          mapUrl: event.mapUrl,
          image: event.image,
          startIso: startDate.toISOString(),
          endIso: endDate.toISOString(),
          duration: formatDurationLabel(startDate, endDate) || event.duration,
          priceLabel: event.price,
          priceAmount: event.priceAmount,
          currency: event.currency || "EUR",
          totalPrice,
          availablePlaces: event.availablePlaces,
          bookedCount: event.bookedCount,
          remainingPlaces: event.remainingPlaces,
          isSelfAdjusting: Boolean(event.isSelfAdjusting),
          quantity: event.quantity || 1,
          equipmentItems: Array.isArray(event.equipmentItems) ? event.equipmentItems : [],
          equipmentSummary: Array.isArray(event.equipmentSummary)
            ? event.equipmentSummary
            : [],
        },
      },
    });
  };

  const scrollEvents = (direction) => {
    const rail = eventsRailRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: rail.clientWidth * 0.88 * direction,
      behavior: "smooth",
    });
  };

  const handleEventsPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const rail = eventsRailRef.current;
    if (!rail) return;
    if (eventsDragRef.current.resetTimer) {
      clearTimeout(eventsDragRef.current.resetTimer);
    }
    eventsDragRef.current = {
      isDragging: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: rail.scrollLeft,
      resetTimer: null,
    };
    rail.classList.add("is-dragging");
  };

  const handleEventsPointerMove = (event) => {
    const rail = eventsRailRef.current;
    if (!rail || !eventsDragRef.current.isDragging) return;
    const deltaX = event.clientX - eventsDragRef.current.startX;
    if (Math.abs(deltaX) > 14) {
      eventsDragRef.current.moved = true;
    }
    rail.scrollLeft = eventsDragRef.current.scrollLeft - deltaX;
  };

  const handleEventsPointerEnd = (event) => {
    const rail = eventsRailRef.current;
    if (!rail) return;
    eventsDragRef.current.isDragging = false;
    rail.classList.remove("is-dragging");
    eventsDragRef.current.resetTimer = setTimeout(() => {
      eventsDragRef.current.moved = false;
      eventsDragRef.current.resetTimer = null;
    }, 0);
  };

  const handleEventOpen = (eventItem) => {
    if (eventsDragRef.current.moved) return;
    setSelectedEvent(eventItem);
  };
  const showEventsSection = eventsLoading || events.length > 0;

  return (
    <div className="info-page">
      <Header />
      <main className="info-content">
        <section className="info-hero">
          <div className="info-hero__copy">
            <p className="info-hero__eyebrow">{t("infoPage.hero.eyebrow", "Plan your paddle")}</p>
            <h1>{t("infoPage.hero.heading", "Everything you need before you hit the water")}</h1>
            <p>{infoHeroDescription}</p>
            <div className="info-hero__actions"></div>
          </div>
          <div className="info-hero__media">
            <div className="info-hero__glow" aria-hidden="true" />
            <img
              src="/assets/pos_info.jpg"
              alt={t("infoPage.hero.imageAlt", "Wayli crew preparing SUP boards by the water")}
            />
          </div>
        </section>

        {showEventsSection && (
        <section className="info-events" id="info-events">
          <header className="info-events__header">
            <div>
              <p>{t("infoPage.events.eyebrow", "Events")}</p>
              <h2>{t("infoPage.events.title", "Upcoming water sessions")}</h2>
            </div>
            <div
              className="info-events__controls"
              aria-label={t("infoPage.events.controls", "Scroll events")}
            >
              <button
                type="button"
                onClick={() => scrollEvents(-1)}
                aria-label={t("infoPage.events.previous", "Previous events")}
              >
                <FaChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scrollEvents(1)}
                aria-label={t("infoPage.events.next", "Next events")}
              >
                <FaChevronRight aria-hidden="true" />
              </button>
            </div>
          </header>

          <div
            className="info-events__viewport"
            ref={eventsRailRef}
            onPointerDown={handleEventsPointerDown}
            onPointerMove={handleEventsPointerMove}
            onPointerUp={handleEventsPointerEnd}
            onPointerCancel={handleEventsPointerEnd}
            onPointerLeave={handleEventsPointerEnd}
          >
            <div className="info-events__grid">
              {eventsLoading ? (
                <p className="contact-hint">
                  {t("infoPage.events.loading", "Loading events...")}
                </p>
              ) : (
                events.map((event) => (
                <article
                  key={event.id}
                  className="info-event-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleEventOpen(event)}
                  onKeyDown={(keyboardEvent) => {
                    if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                      keyboardEvent.preventDefault();
                      setSelectedEvent(event);
                    }
                  }}
                >
                  <div className="info-event-card__image">
                    <img src={event.image} alt={event.title} onError={handleEventImageError} />
                  </div>
                  <div className="info-event-card__body">
                    <div className="info-event-card__meta">
                      <span>
                        <LuCalendarDays aria-hidden="true" />
                        {event.scheduleLabel || formatEventDate(event.startDate, language)}
                      </span>
                      <span>{event.price}</span>
                    </div>
                    <h3>{event.title}</h3>
                    <p>{event.description}</p>
                    <div className="info-event-card__footer">
                      <span>{event.location}</span>
                      <button
                        type="button"
                        onClick={(clickEvent) => {
                          clickEvent.stopPropagation();
                          if (!eventsDragRef.current.moved) {
                            setSelectedEvent(event);
                          }
                        }}
                      >
                        {t("infoPage.events.book", "Book now")}
                      </button>
                    </div>
                  </div>
                </article>
                ))
              )}
            </div>
          </div>
        </section>
        )}

        <section className="info-card info-location-hub" id="info-locations">
          <header>
            <h2>{t("infoPage.map.title", "Our launch spots")}</h2>
            <p>
              {t(
                "infoPage.map.description",
                "Pick the base that suits your mood — see the map and today's hours."
              )}
            </p>
          </header>
          <div className="info-location-hub__layout">
            <div className="info-location-hub__column info-location-hub__column--left">
              <div className="map-shell">
                <GoogleMapSection
                  locations={locations}
                  selectedLocationId={selectedLocationId}
                  onSelectLocation={setSelectedLocationId}
                />
              </div>
              {locationsLoading && (
                <p className="contact-hint">
                  {t("infoPage.locations.loading", "Loading locations...")}
                </p>
              )}
              {locationsError && (
                <p className="contact-error" role="alert">
                  {locationsError}
                </p>
              )}
              {!useFallback && !locations.length && !locationsLoading && (
                <p className="contact-hint">
                  {t(
                    "infoPage.locations.empty",
                    "No active launch spots are available right now."
                  )}
                </p>
              )}
              <ul className="location-picker">
                {locations.map((loc) => (
                  <li key={loc.id}>
                    <button
                      type="button"
                      className={`location-picker__btn ${
                        selectedLocationId === loc.id ? "is-active" : ""
                      }`.trim()}
                      onClick={() => setSelectedLocationId(loc.id)}
                      aria-pressed={selectedLocationId === loc.id}
                    >
                      <span className="location-picker__dot" aria-hidden="true" />
                      <span className="location-picker__text">
                        <strong>{loc.name}</strong>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="info-location-hub__column info-location-hub__column--right">
              {selectedLocation ? (
                <article
                  className={`location-hours__card is-featured accent-${
                    selectedLocation.accent ?? "base"
                  }`.trim()}
                >
                  <div className="location-hours__header">
                    {selectedLocationMapUrl ? (
                      <a
                        className="location-hours__pin"
                        href={selectedLocationMapUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t("infoPage.locations.openMap", "Open in Google Maps")}
                        title={t("infoPage.locations.openMap", "Open in Google Maps")}
                      >
                        <RiMapPin2Line aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="location-hours__pin">
                        <RiMapPin2Line aria-hidden="true" />
                      </span>
                    )}
                    <div className="location-hours__meta">
                      {selectedLocation.highlight && (
                        <span className="location-hours__highlight">
                          {selectedLocation.highlight}
                        </span>
                      )}
                      <h3>{selectedLocation.name}</h3>
                      <p>{selectedLocation.address}</p>
                    </div>
                  </div>
                  {selectedLocation.operatingHours ? (
                    <div className="location-hours__schedule">
                      <div className="location-hours__schedule-label">
                        {t("infoPage.locations.scheduleLabel", "Working hours")}
                      </div>
                      <ul className="location-hours__rows">
                        {(operatingHoursLines.length
                          ? operatingHoursLines
                          : [selectedLocation.operatingHours]
                        ).map((line) => {
                          const { day, time } = splitOperatingHoursLine(line);
                          return (
                            <li className="location-hours__row" key={line}>
                              <span className="location-hours__day">{day}</span>
                              {time ? (
                                <span className="location-hours__time">{time}</span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="location-hours__note tone-muted">
                      {t(
                        "infoPage.locations.scheduleMissing",
                        "Operating hours not available."
                      )}
                    </p>
                  )}
                  {selectedLocation.note && (
                    <p
                      className={`location-hours__note tone-${
                        selectedLocation.noteTone ?? "soft"
                      }`.trim()}
                    >
                      {selectedLocation.note}
                    </p>
                  )}
                </article>
              ) : null}
              {!!contactDetails.length && (
                <div
                  className={`location-hours__contact-block accent-${
                    selectedLocation?.accent ?? "base"
                  }`.trim()}
                >
                  <div className="location-hours__contact-header">
                    <h4>{t("infoPage.contact.title", "Stay in touch")}</h4>
                    <p>
                      {t(
                        "infoPage.contact.description",
                        "Need to tweak your booking, add extra boards, or ask for a guide? We’re one tap away."
                      )}
                    </p>
                  </div>
                  <ul className="location-hours__contact">
                    {contactDetails.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.value}>
                          <a
                            href={item.href}
                            target={item.href.startsWith("http") ? "_blank" : undefined}
                            rel="noreferrer"
                            aria-label={`${item.label}: ${item.value}`}
                          >
                            <span className="location-hours__contact-icon">
                              <Icon aria-hidden="true" />
                            </span>
                            <span className="location-hours__contact-value">{item.value}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="info-highlights">
          {INFO_HIGHLIGHTS.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.titleKey} className="info-highlight">
                <div className="info-highlight__icon">
                  <Icon aria-hidden="true" />
                </div>
                <div className="info-highlight__body">
                  <h3>{t(item.titleKey, item.titleFallback)}</h3>
                  <p>{t(item.descriptionKey, item.descriptionFallback)}</p>
                </div>
              </article>
            );
          })}
        </section>
      </main>
      {selectedEvent && (
        <EventBookingModal
          event={selectedEvent}
          language={language}
          onClose={() => setSelectedEvent(null)}
          onCheckout={handleEventCheckout}
          t={t}
        />
      )}
      <Footer />
    </div>
  );
};

export default InfoPage;
